import React, { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import type { SetImageOptions } from '@tiptap/extension-image'
import { Dialog, Button, TextField, Text, Flex } from '@radix-ui/themes'
import { isAllowedUri } from '@tiptap/extension-link'
import { isValidMediaUrl } from '@disco/utils/links'
import { UploadIcon } from '../icons'
import styles from '../toolbar.module.css'

type SetImageArgs = Omit<SetImageOptions, 'width' | 'height'> & {
  width?: string
  height?: string
  link?: string
}

const MAX_IMAGE_DIMENSION = 10_000

/** Parse a dimension string to a valid integer in [1, MAX_IMAGE_DIMENSION], or null. */
function parseDimension(value: string): number | null {
  if (!value) return null
  const n = Number(value)
  if (
    !Number.isFinite(n) ||
    n < 1 ||
    n > MAX_IMAGE_DIMENSION ||
    n !== Math.floor(n)
  )
    return null
  return n
}

const MAX_FILE_SIZE_MB = 10
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
const ALLOWED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
])

export function ImageInsertDialog({
  open,
  onOpenChange,
  editor,
  onImageUpload,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editor: Editor
  onImageUpload?: (file: File) => Promise<string>
}) {
  const [src, setSrc] = useState('')
  const [alt, setAlt] = useState('')
  const [link, setLink] = useState('')
  const [width, setWidth] = useState('')
  const [height, setHeight] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !onImageUpload) return
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, WebP, SVG).')
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    setUploading(true)
    setError('')
    try {
      const url = await onImageUpload(file)
      if (!mountedRef.current) return
      if (!isValidMediaUrl(url)) {
        setError('Invalid image URL returned.')
        return
      }
      setSrc(url)
    } catch {
      if (!mountedRef.current) return
      setError('Failed to upload image. Please try again.')
    } finally {
      if (mountedRef.current) {
        setUploading(false)
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleSubmit = () => {
    if (!src.trim()) {
      setError('Please provide an image URL or upload an image.')
      return
    }
    if (!isValidMediaUrl(src.trim())) {
      setError('Please enter a valid URL starting with http:// or https://')
      return
    }
    if (link.trim() && !isAllowedUri(link.trim())) {
      setError(
        'Please enter a valid link URL starting with http://, https://, or mailto:',
      )
      return
    }

    const parsedWidth = parseDimension(width.trim())
    const parsedHeight = parseDimension(height.trim())
    if (width.trim() && parsedWidth === null) {
      setError('Width must be a number between 1 and 10000.')
      return
    }
    if (height.trim() && parsedHeight === null) {
      setError('Height must be a number between 1 and 10000.')
      return
    }

    const imageAttrs: SetImageArgs = {
      src: src.trim(),
      ...(alt.trim() && { alt: alt.trim() }),
      ...(parsedWidth && { width: String(parsedWidth) }),
      ...(parsedHeight && { height: String(parsedHeight) }),
      ...(link.trim() && { link: link.trim() }),
    }

    editor
      .chain()
      .focus()
      .setImage(imageAttrs as unknown as SetImageOptions)
      .run()
    handleClose()
  }

  const handleClose = () => {
    onOpenChange(false)
    setSrc('')
    setAlt('')
    setLink('')
    setWidth('')
    setHeight('')
    setUploading(false)
    setError('')
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={v => {
        if (!v) handleClose()
        else onOpenChange(v)
      }}
    >
      <Dialog.Content maxWidth="450px">
        <Dialog.Title>Insert Image</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Enter an image URL or upload an image file.
        </Dialog.Description>
        <Flex direction="column" gap="3">
          {onImageUpload && (
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                Upload
              </Text>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={handleFileSelect}
                className={styles.fileInput}
              />
              <button
                type="button"
                className={`${styles.uploadArea} ${
                  uploading ? styles.uploadAreaUploading : ''
                }`}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <UploadIcon />
                <span>
                  {uploading ? 'Uploading...' : 'Click to upload image'}
                </span>
              </button>
            </Flex>
          )}
          <Flex direction="column" gap="1">
            <Text as="label" htmlFor="img-src" size="2" weight="medium">
              Image URL
            </Text>
            <TextField.Root
              id="img-src"
              value={src}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSrc(e.target.value)
                setError('')
              }}
              placeholder="https://example.com/image.png"
            />
          </Flex>
          <Flex direction="column" gap="1">
            <Text as="label" htmlFor="img-alt" size="2" weight="medium">
              Alt Text
            </Text>
            <TextField.Root
              id="img-alt"
              value={alt}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAlt(e.target.value)
              }
              placeholder="Image description"
            />
          </Flex>
          <Flex direction="column" gap="1">
            <Text as="label" htmlFor="img-link" size="2" weight="medium">
              Link
            </Text>
            <TextField.Root
              id="img-link"
              value={link}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLink(e.target.value)
              }
              placeholder="https://example.com"
            />
          </Flex>
          <div className={styles.dimensionRow}>
            <Flex direction="column" gap="1">
              <Text as="label" htmlFor="img-width" size="2" weight="medium">
                Width
              </Text>
              <TextField.Root
                id="img-width"
                value={width}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setWidth(e.target.value)
                }
                placeholder="e.g. 600"
              />
            </Flex>
            <Flex direction="column" gap="1">
              <Text as="label" htmlFor="img-height" size="2" weight="medium">
                Height
              </Text>
              <TextField.Root
                id="img-height"
                value={height}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setHeight(e.target.value)
                }
                placeholder="e.g. 400"
              />
            </Flex>
          </div>
          {error && (
            <Text color="red" size="1">
              {error}
            </Text>
          )}
        </Flex>
        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button variant="solid" onClick={handleSubmit} disabled={uploading}>
            Insert
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
